<script setup>

import { defineProps, defineEmits, computed, watch, ref} from "vue";
import BarraNavegacao from "./BarraNavegacao.vue";
import DadoConteudo from "./DadoConteudo.vue";
import UtilNumber from "@/utils/UtilNumber";
import UtilObject from "@/utils/UtilObject";

//==================================================================//
//========================= Props & Emits ==========================//
//==================================================================//

const props = defineProps([
    "usuarioStore",
    "colaboradorStore",
    "controlesDesativados",
    "vincularNotaFiscal",
    "notasFiscaisVinculaveis",
    "dadoOriginal",
]);

const emits = defineEmits([
    "update:alteraControlesDesativados",
    "update:alteraVincularNotaFiscal",
]);

//==================================================================//
//============================= Dado ===============================//
//==================================================================//

const dado = ref({});

//==================================================================//
//==================== Recebimento Novo ============================//
//==================================================================//

const recebimentoNovo = ref({});

//==================================================================//
//================= Altera Controles Desativados ===================//
//==================================================================//

const alteraControlesDesativados = (update)=>{

    emits('update:alteraControlesDesativados',update);

};

//==================================================================//
//================= Altera Vincular Nota Fiscal ====================//
//==================================================================//

const alteraVincularNotaFiscal = (update) =>{

    emits('update:alteraVincularNotaFiscal',update);

};

//==================================================================//
//=============== Conta a Receber Valor Original ===================//
//==================================================================//

const contaAReceberValorOriginal = computed(()=>{

    return UtilNumber.toNumber(dado?.value?.valorOriginal);

});

//==================================================================//
//=============== Conta a Receber Valor Vinculado ==================//
//==================================================================//

const contaAReceberValorVinculado = computed(()=>{

    let valorVinculado = dado?.value?.valorRecebido + recebimentoNovo?.value?.valorRecebimento;

    return UtilNumber.toNumber(valorVinculado);

});

//==================================================================//
//=============== Conta a Receber Saldo a Vincular =================//
//==================================================================//

const contaAReceberSaldoAVincular = computed(()=>{

    let saldoAVincular = dado?.value?.valorParaReceber - recebimentoNovo?.value?.valorRecebimento;

    return UtilNumber.toNumber(saldoAVincular);

});

//==================================================================//
//============= Nota Fiscal Emitida Valor Original =================//
//==================================================================//

const notaFiscalEmitidaValorOriginal = computed(()=>{

    return UtilNumber.toNumber(recebimentoNovo?.value?.notaFiscalEmitida?.valorOriginal);

});

//==================================================================//
//============== Nota Fiscal Emitida Valor Recebido ================//
//==================================================================//

const notaFiscalEmitidaValorRecebido = computed(()=>{

    let valorRecebido = recebimentoNovo?.value?.notaFiscalEmitida?.valorRecebido + recebimentoNovo?.value?.valorRecebimento;

    return UtilNumber.toNumber(valorRecebido);

});

//==================================================================//
//=========== Nota Fiscal Emitida Valor para Receber ===============//
//==================================================================//

const notaFiscalEmitidaValorParaReceber = computed(()=>{

    let valorParaReceber = recebimentoNovo?.value?.notaFiscalEmitida?.valorParaReceber - recebimentoNovo?.value?.valorRecebimento;

    return UtilNumber.toNumber(valorParaReceber);

});

//==================================================================//
//===================== Altera Dado de Compra ======================//
//==================================================================//

const alteraDado = (update = {})=>{

    dado.value = UtilObject.merge(dado.value, update)

};

//==================================================================//
//=================== Armazena Recebimento Novo ====================//
//==================================================================//

const armazenaRecebimentoNovo = (update)=>{

    recebimentoNovo.value = {
        ...recebimentoNovo.value,
        ...update
    }

};

//==================================================================//
//===================== Altera Recebimento Novo ====================//
//==================================================================//

const alteraRecebimentoNovo = (update)=>{

    armazenaRecebimentoNovo(update);

    recebimentoNovo.value.valorRecebimento = UtilNumber.getValueFromPercentage({
        porcentagem: UtilNumber.toNumber(recebimentoNovo?.value?.porcentagemRecebimento),
        valor: UtilNumber.toNumber(recebimentoNovo?.value?.notaFiscalEmitida?.valorParaReceber)
    });

};

//==================================================================//
//================== Autocalcula Recebimento Novo ==================//
//==================================================================//

const autocalculaRecebimentoNovo = (update)=>{

    armazenaRecebimentoNovo(update);

    if(!recebimentoNovo.value.autocalculado){
        return;
    }

    let porcentagemRecebimento = UtilNumber.getPercentage({
        parte: dado?.value?.valorParaReceber,
        total: recebimentoNovo?.value?.notaFiscalEmitida?.valorParaReceber,
        maximo: 100,
        arredondar: false,
    });

    recebimentoNovo.value.valorRecebimento = UtilNumber.getValueFromPercentage({
        porcentagem: porcentagemRecebimento,
        valor: recebimentoNovo?.value?.notaFiscalEmitida?.valorParaReceber
    });

    recebimentoNovo.value.porcentagemRecebimento = UtilNumber.round(porcentagemRecebimento);

};

//==================================================================//
//======================== Prepara Modal ===========================//
//==================================================================//

const preparaModal = ()=>{

    preparaDado(UtilObject.clone(props?.dadoOriginal));
    preparaRecebimentoNovo();

}

//==================================================================//
//========================= Prepara Dado ===========================//
//==================================================================//

const preparaDado = (dado)=>{



    alteraDado(dado);

};

//==================================================================//
//=================== Prepara Recebimento Novo =====================//
//==================================================================//

const preparaRecebimentoNovo = ()=>{
    
    armazenaRecebimentoNovo({
        notaFiscalEmitida:{},
        porcentagemRecebimento:0,
        valorRecebimento:0,
        autocalculado: false,
    });

};

//==================================================================//
//==================== Watch => vincularNotaFiscal =================//
//==================================================================//

watch(()=> props.vincularNotaFiscal, () => {

    if(props.vincularNotaFiscal == true){
        preparaModal();
    }

},{ immediate: true });

</script>

<template>

    <main>

        <v-dialog
            content-class="c-dialog"
            v-model="props.vincularNotaFiscal"
            fullscreen
            transition="dialog-bottom-transition"
            persistent
        >

            <v-card class="c-modal">

                <BarraNavegacao
                    :usuarioStore="props.usuarioStore"
                    :colaboradorStore="props.colaboradorStore"
                    :vincularNotaFiscal="props.vincularNotaFiscal"
                    :controlesDesativados="controlesDesativados"
                    :dado="dado"
                    :recebimentoNovo="recebimentoNovo"
                    :contaAReceberValorOriginal="contaAReceberValorOriginal"
                    :contaAReceberValorVinculado="contaAReceberValorVinculado"
                    :contaAReceberSaldoAVincular="contaAReceberSaldoAVincular"
                    :notaFiscalEmitidaValorRecebido="notaFiscalEmitidaValorRecebido"
                    :notaFiscalEmitidaValorParaReceber="notaFiscalEmitidaValorParaReceber"
                    :notaFiscalEmitidaValorOriginal="notaFiscalEmitidaValorOriginal"
                    @update:alteraControlesDesativados="alteraControlesDesativados"
                    @update:alteraVincularNotaFiscal="alteraVincularNotaFiscal"
                ></BarraNavegacao>

                <DadoConteudo
                    :vincularNotaFiscal="props.vincularNotaFiscal"
                    :controlesDesativados="props.controlesDesativados"
                    :notasFiscaisVinculaveis="props.notasFiscaisVinculaveis"
                    :dado="dado"
                    :recebimentoNovo="recebimentoNovo"
                    :contaAReceberValorOriginal="contaAReceberValorOriginal"
                    :contaAReceberValorVinculado="contaAReceberValorVinculado"
                    :contaAReceberSaldoAVincular="contaAReceberSaldoAVincular"
                    :notaFiscalEmitidaValorRecebido="notaFiscalEmitidaValorRecebido"
                    :notaFiscalEmitidaValorParaReceber="notaFiscalEmitidaValorParaReceber"
                    :notaFiscalEmitidaValorOriginal="notaFiscalEmitidaValorOriginal"
                    @update:alteraControlesDesativados="alteraControlesDesativados"
                    @update:alteraRecebimentoNovo="alteraRecebimentoNovo"
                    @update:autocalculaRecebimentoNovo="autocalculaRecebimentoNovo"
                ></DadoConteudo>
            
            </v-card>

        </v-dialog>

    </main>

</template>

<style scoped>

.v-dialog__content >>> .v-dialog{
    background-color: white;
}

</style>

